import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Gift } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dd7e4899-b441-40b2-8a4d-ff26ed1613bb/artifacts/tc6miqnj_1000017045.jpg";
const BG_IMG = "https://images.unsplash.com/photo-1658935289883-614e7392e7b1?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function AuthPage() {
  const { login, register, API } = useAuth();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';

  const [isLogin, setIsLogin] = useState(!refCode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referrerName, setReferrerName] = useState('');

  // Validate referral code on load
  useEffect(() => {
    if (refCode) {
      axios.get(`${API}/referral/validate/${refCode}`)
        .then(res => setReferrerName(res.data.referrer_name))
        .catch(() => {});
    }
  }, [refCode, API]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(name, email, password, refCode);
        toast.success('Welcome! Your free month has started.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG_IMG} alt="Horse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1B4332]/40"></div>
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-white mb-3 drop-shadow-lg">
            Your Complete Equine Care Companion
          </h2>
          <p className="text-white/80 text-base leading-relaxed">
            Track health records, manage barn operations, and keep everything organized in one place.
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 bg-[#F2F7F4]">
        <Link to="/" className="flex items-center gap-2 text-[#4B6B58] text-sm mb-8 hover:text-[#1B4332] btn-lift w-fit" data-testid="back-to-home">
          <ArrowLeft size={16} /> Back to home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="Stable Solutions" className="h-12 w-12 rounded-full object-cover shadow-[0_4px_12px_rgb(27,67,50,0.08)]" />
          <div>
            <span className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] block">Stable Solutions</span>
            <span className="text-[10px] font-medium tracking-wider text-[#4B6B58] uppercase">The Oaks Legacy Ranch</span>
          </div>
        </div>

        <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332] mb-2">
          {isLogin ? 'Welcome Back' : 'Start Your Free Month'}
        </h1>
        <p className="text-[#4B6B58] mb-8">
          {isLogin ? 'Sign in to manage your equine records.' : 'First month free. Then $9.95/mo for your first year. Cancel anytime.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5 max-w-md" data-testid="auth-form">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1.5">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your full name"
                required
                data-testid="auth-name-input"
                className="stable-input w-full"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#122A1F] mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              data-testid="auth-email-input"
              className="stable-input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#122A1F] mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                minLength={6}
                data-testid="auth-password-input"
                className="stable-input w-full pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B6B58]"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-button"
            className="w-full py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] disabled:opacity-50"
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Start Free Month')}
          </button>
        </form>

        <p className="mt-6 text-sm text-[#4B6B58]">
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <button
            onClick={() => setIsLogin(!isLogin)}
            data-testid="auth-toggle-mode"
            className="font-semibold text-[#1B4332] hover:underline"
          >
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
}
